import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { API, graphqlOperation } from "aws-amplify";
import { CallFuncOperateFee } from "src/graphql/queries";
import { type RootState } from "src/common/rootState.type";

import {
  UNIDENTIFIED,
  calculateFee,
} from "@cielo-blu-team/amazon-fee-lib-v2";

/** 販売価格変更時のデバウンス時間（ms） */
const DEBOUNCE_MS = 400;

/**
 * 販売手数料を取得・計算するカスタムフック。
 *
 * - ASIN 変更時に GraphQL `CallFuncOperateFee(type: "get-referral-fee")` で
 *   `{ fee: ReferralAndClosingFee, patternId }` を取得
 * - patternId が有効（"A1"〜"D4"）かつ Redux に referralFee.patterns があれば、
 *   価格変動時は `calculateFee` でクライアント側計算（API なし）
 * - patternId が "unknown" / null / patterns 未取得のときは API レスポンスの内訳をそのまま採用し、
 *   価格変動時は 400ms デバウンスで API 再取得
 *
 * 戻り値の `referralFee` / `closingFee` は税抜、`referralFeeIncTax` は
 * 「販売手数料 + カテゴリ成約料」の税込合計。
 */
export const useReferralFee = (): {
  referralFee: number | undefined;
  closingFee: number | undefined;
  referralFeeIncTax: number | undefined;
  isLoading: boolean;
  hasError: boolean;
  updateBySellPrice: (sellPrice: number) => void;
} => {
  const { product } = useSelector((state: RootState) => state.top);
  const referralFeePatterns = useSelector(
    (state: RootState) => state.feePatterns.referralFee
  );

  const [referralFee, setReferralFee] = useState<number | undefined>(undefined);
  const [closingFee, setClosingFee] = useState<number | undefined>(undefined);
  const [referralFeeIncTax, setReferralFeeIncTax] = useState<
    number | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // 直近で取得した patternId（クライアント計算可否の判定に使う）
  const patternIdRef = useRef<string | null | undefined>(undefined);
  // デバウンス用タイマーID
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  // 古いリクエストの結果が新しい結果を上書きしないようにするためのリクエストID
  const requestIdRef = useRef<number>(0);

  /** State を全てクリア */
  const resetFees = (): void => {
    setReferralFee(undefined);
    setClosingFee(undefined);
    setReferralFeeIncTax(undefined);
  };

  /** patternId が自前計算可能か */
  const canCalculateLocally = (
    patternId: string | null | undefined
  ): patternId is string => {
    return (
      typeof patternId === "string" &&
      patternId !== UNIDENTIFIED &&
      referralFeePatterns?.patterns?.[patternId] !== undefined
    );
  };

  /**
   * patternId と価格から内訳を算出して State に反映。
   * 呼び出し元で patternId 有効性をチェック済みの前提。
   */
  const applyLocalCalculation = (patternId: string, price: number): void => {
    if (!referralFeePatterns) return;
    const result = calculateFee(referralFeePatterns.patterns, patternId, price);
    setReferralFee(result.referralFee);
    setClosingFee(result.closingFee);
    setReferralFeeIncTax(result.totalFeeIncTax);
  };

  /** API を呼び出して販売手数料の内訳と patternId を取得 */
  const fetchReferralFee = async (
    asin: string | undefined,
    price: number | undefined
  ): Promise<void> => {
    if (!asin || !price || price <= 0) {
      resetFees();
      setIsLoading(false);
      setHasError(false);
      patternIdRef.current = undefined;
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setHasError(false);

    try {
      const { data: res }: any = await API.graphql(
        graphqlOperation(CallFuncOperateFee, {
          type: "get-referral-fee",
          asin,
          price,
        })
      );

      if (myRequestId !== requestIdRef.current) return;

      const parsed: any = JSON.parse(res.CallFuncOperateFee);

      if (parsed?.errorCode) {
        resetFees();
        setHasError(true);
        patternIdRef.current = undefined;
        return;
      }

      const patternId: string | null = parsed?.patternId ?? null;
      patternIdRef.current = patternId;
      setHasError(false);

      if (canCalculateLocally(patternId)) {
        // パターン有効: クライアント側でも同じロジックで内訳を再計算（サーバーレスポンスと一致するはず）
        applyLocalCalculation(patternId, price);
      } else {
        // パターン不明: サーバーレスポンスの内訳をそのまま採用
        const fee = parsed?.fee;
        setReferralFee(fee?.referralFee);
        setClosingFee(fee?.closingFee);
        setReferralFeeIncTax(fee?.totalFeeIncTax);
      }
    } catch (error) {
      if (myRequestId !== requestIdRef.current) return;
      console.log(error);
      resetFees();
      setHasError(true);
      patternIdRef.current = undefined;
    } finally {
      if (myRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // product 変更時に初回取得
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    void fetchReferralFee(product?.asin, Number(product?.actPrice));
    // fetchReferralFee の参照は product 変更時のみ更新したいので意図的に依存に含めない
  }, [product]);

  /**
   * 販売価格を指定して再計算。
   *
   * - patternId が有効 & referralFee.patterns あり → 即座にクライアント計算
   * - それ以外 → 400ms デバウンスで API 再取得
   */
  const updateBySellPrice = (sellPrice: number): void => {
    if (!sellPrice || sellPrice <= 0) {
      resetFees();
      return;
    }

    const patternId = patternIdRef.current;
    if (canCalculateLocally(patternId)) {
      applyLocalCalculation(patternId, sellPrice);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      void fetchReferralFee(product?.asin, sellPrice);
    }, DEBOUNCE_MS);
  };

  return {
    referralFee,
    closingFee,
    referralFeeIncTax,
    isLoading,
    hasError,
    updateBySellPrice,
  };
};
